import {action, computed, observable, toJS, isObservableArray} from "mobx";
import {observableMap} from "util/mobx";
import FormApiStorageProvider from "./storage/FormApiStorageProvider";
import BaseStorageProvider from "./storage/BaseStorageProvider";
import isUndefined from "lodash/isUndefined";
import isFunction from "lodash/isFunction";
import transform from "lodash/transform";
import mapValues from "lodash/mapValues";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import forEach from "lodash/forEach";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import reduce from "lodash/reduce";
import isNull from "lodash/isNull";
import filter from "lodash/filter";
import split from "lodash/split";
import some from "lodash/some";
import find from "lodash/find";
import keys from "lodash/keys";
import Emitter from "emmett";
import uniqid from "uniqid";

export default class FormModel {
    // data should be either scalar values or Arrays of forms (i.e. <key>: <value> | [Form])
    @observable data = observable.map();

    @observable _originalData = observable.map();
    @observable _validationComputeds = observable.map();
    @observable _doneFields = observable.map();
    @observable _serverErrors = observable.map();
    @observable _errorFieldsFromServer = observable.map();

    constructor(props, storageProvider, validations) {
        if (!isUndefined(props.data)) {
            this.data = observable.map(props.data);
            this.setOriginalData();
        }

        if (!isUndefined(props.errors)) {
            this._serverErrors = observable.map(props.errors);
        }

        this.parent = isUndefined(props.parent) ? null : props.parent;

        // set up backing storage
        if (isUndefined(storageProvider) || isNull(storageProvider)) {
            if (!isEmpty(props.endpoint) || !isEmpty(props.deleteEndpoint)) {
                const onFormErrorCallback = (errors) => {
                    if (isFunction(this.validationErrorCallback)) {
                        this.validationErrorCallback(errors);
                    }
                    // eslint-disable-next-line lodash/prefer-lodash-method
                    this._serverErrors.clear();
                    this._serverErrors.merge(errors);
                    this._processErrorsFromServer();
                    this.onFormError();
                };
                this.storageProvider = new FormApiStorageProvider(props.endpoint, props.deleteEndpoint, onFormErrorCallback.bind(this));
            } else {
                this.storageProvider = new BaseStorageProvider();
            }
        } else {
            this.storageProvider = storageProvider;
        }

        // set up form emitter
        this.emitter = new Emitter();
        this.key = uniqid();

        this._setupComputedValidationErrors(validations);
    }

    _setupComputedValidationErrors = validations => {
        const computedValidationErrors = transform(validations, (accumulator, validation, fieldName) => {
            accumulator[fieldName] = computed(() => validation.validate(fieldName, this));
        });
        // eslint-disable-next-line lodash/prefer-lodash-method
        this._validationComputeds.clear();
        this._validationComputeds.merge(computedValidationErrors);
    };

    @computed
    get serverErrors() {
        const errors = new Map();
        // eslint-disable-next-line lodash/prefer-lodash-method
        this._serverErrors.forEach(
            (errorMessages, fieldName) => {
                const currentValue = this.data.get(fieldName);
                const comparedValue = toJS(this._errorFieldsFromServer.get(fieldName));
                const clonedCurrentValue = toJS(currentValue);
                if (!isUndefined(currentValue) && !isUndefined(comparedValue)
                    // field data has changed since getting an error back from the server
                    && !isEqual(clonedCurrentValue, comparedValue)
                ) {
                    return;
                }
                errors.set(fieldName, errorMessages);
            }
        );
        return errors;
    }

    @computed
    get errors() {
        const errors = new Map();
        // eslint-disable-next-line lodash/prefer-lodash-method
        this.serverErrors.forEach((errorMessages, fieldName) => errors.set(fieldName, errorMessages));
        // eslint-disable-next-line lodash/prefer-lodash-method
        this.computedValidationErrors.forEach((computedValidationError, fieldName) => {
            if (this._doneFields.has(fieldName)) {
                errors.set(fieldName, computedValidationError);
            }
        });
        return errors;
    }

    @computed
    get computedValidationErrors() {
        const errors = observable.map();
        // eslint-disable-next-line lodash/prefer-lodash-method
        this._validationComputeds.forEach((validationComputed, fieldName) => {
            const errorMessages = validationComputed.get();
            if (find(errorMessages, errorMessage => errorMessage)) {
                errors.set(fieldName, errorMessages);
            }
        });
        return errors;
    }

    get validationSteps() {
        return {};
    }

    setOriginalData() {
        this._originalData = observable.map(this.getData());
    }

    getData() {
        return this._getDataHelper(this.data);
    }

    getOriginalData() {
        return this._getDataHelper(this._originalData);
    }

    // re-implement observable map toJS(), except with sub-form handling for forms with arrays
    _getDataHelper(data) {
        let jsonData = {};
        // eslint-disable-next-line lodash/prefer-lodash-method
        data.forEach((value, key) => {
            let isSubformArray = false;
            if (FormModel._isSubform(value)) {
                jsonData[key] = value.getData();
            } else {
                if (FormModel._isSubformArray(value)) {
                    // arrays in forms can be sub-forms
                    jsonData[key] = [];
                    forEach(value, (subform) => {
                        if (isFunction(subform.getData)) {
                            isSubformArray = true;
                            jsonData[key].push(subform.getData());
                        }
                    });
                }
                if (!isSubformArray) {
                    // basic toJS() case
                    jsonData[key] = toJS(value);
                }
            }
        });
        return jsonData;
    }

    @computed
    get canSubmit() {
        return Boolean(!this.hasErrors && this.hasChanges);
    }

    @computed
    get hasChanges() {
        const originalDataKeys = observableMap.keys(this._originalData);
        const currentDataKeysThatAreNew = filter(observableMap.keys(this.data), key => !includes(originalDataKeys, key));
        const originalDataChanges = find(originalDataKeys, fieldName => this.hasChanged(this._originalData.get(fieldName), this.data.get(fieldName), fieldName));
        const currentDataChanges = find(currentDataKeysThatAreNew, fieldName => this.hasChanged(this._originalData.get(fieldName), this.data.get(fieldName), fieldName));
        return originalDataChanges || currentDataChanges;
    }

    hasChanged(fieldValue1, fieldValue2, fieldName) {
        if (fieldValue2 instanceof FormModel) {
            return fieldValue2.hasChanges;
        }
        if (isObservableArray(fieldValue1)) {
            if (isNull(fieldValue2) || fieldValue1.length !== fieldValue2.length) {
                return true;
            }
            return some(toJS(fieldValue1), (field, key) => {
                return this.hasChanged(fieldValue1[key], fieldValue2[key]);
            });
        }
        if (fieldValue1 instanceof Object && fieldValue2 instanceof Object) {
            return some(keys(toJS(fieldValue1)), (key) => {
                return this.hasChanged(fieldValue1[key], fieldValue2[key], key);
            });
        }
        if (isEqual(Number(fieldValue1), 0) && isEqual(Number(fieldValue2), 0)) {
            return false;
        }
        return fieldValue1 !== fieldValue2;
    }

    @computed
    get hasErrors() {
        return this.errors.size > 0;
    }

    @computed
    get allStepErrors() {
        return mapValues(this.validationSteps, mapKeys => this.hasErrorsForKeys(mapKeys));
    }

    hasErrorsForKeys(mapKeys) {
        return find(mapKeys, key => this.errors.has(key));
    }

    @computed
    get isEmpty() {
        return reduce(observableMap.values(this.data), (result, value) => {
            if (FormModel._isSubform(value)) {
                return value.isEmpty;
            } else if (FormModel._isSubformArray(value)) {
                return result && reduce(value, (subResult, subform) => subResult && subform.isEmpty, true);
            }
            const isFieldEmpty = isUndefined(value) || isNull(value) || (typeof value === "string" && isEmpty(value));
            return result && isFieldEmpty;
        }, true);
    }

    @action
    setErrors(errors) {
        this._serverErrors.clear();
        this._serverErrors.merge(errors);
        this._processErrorsFromServer();
        this.onFormError(); // trigger form error handler if set
    }

    /* Warning: Will null-out non-nullable fields on a form */
    @action
    clear(fieldsToPreserve) {
        const newData = new Map();
        this._doneFields.clear();
        forEach(fieldsToPreserve, fieldName => {
            newData.set(fieldName, this.data.get(fieldName));
            this._doneFields.set(fieldName, true);
        });
        // eslint-disable-next-line lodash/prefer-lodash-method
        this.replaceData(newData);
    }

    /* Preferred over clear() in cases where form has non-nullable or pre-set values */
    @action
    resetToDefault() {
        this.replaceData(this._originalData);
    }

    @action
    replaceData(data) {
        // eslint-disable-next-line lodash/prefer-lodash-method
        this.data.clear();
        this.data.merge(data);
    }

    @action
    markStepDone(stepKey) {
        if (!this.validationSteps[stepKey]) {
            throw new Error(`${stepKey} validation step does not exist`);
        }

        forEach(this.validationSteps[stepKey], field => this.markFieldDone(field));
    }

    @action
    markFieldDone(fieldName) {
        this._doneFields.set(fieldName, true);

        // NOTE: this will result in a subform saying that a field that doesn't actually exist in that subform
        // has been touched because that field exists in the parent
        // This is okay if we assume that field names are unique across parents and subforms
        this._callFunctionOnSubForm("markFieldDone", fieldName);
    }

    @action
    validateStep(stepKey) {
        if (!this.validationSteps[stepKey]) {
            throw new Error(`${stepKey} validation step does not exist`);
        }
        forEach(this.validationSteps[stepKey], field => this.markFieldDone(field));
    }

    @action
    allDone() {
        // eslint-disable-next-line lodash/prefer-lodash-method
        this._validationComputeds.forEach((v, k) => this.markFieldDone(k));
    }

    @action
    save(dispatcher, successCallback, validationErrorCallback) {
        this._serverErrors.clear();
        this.allDone();

        // Some errors like loan account numbers do not trigger
        // until the server-side submit so this.hasErros is never set
        if (isFunction(validationErrorCallback)) {
            this.validationErrorCallback = validationErrorCallback;
        }

        if (this.hasErrors) {
            isFunction(validationErrorCallback) && validationErrorCallback();
            return;
        }

        const wrappedSuccessCallback = (err, res) => {
            if (successCallback) {
                successCallback(err, res);
            }
            if (!isEmpty(this.getOriginalData())) {
                this.setOriginalData();
            }
        };

        this.storageProvider.save(dispatcher, this.getData(), wrappedSuccessCallback);
    }

    @action
    delete(dispatcher, callback) {
        this.storageProvider.delete(dispatcher, callback);
    }

    get(fieldName) {
        return this.data.get(fieldName);
    }

    // only need to call this method if updating a form field from outside
    // the form's UI and we need to react to that change
    @action
    set(fieldName, fieldValue) {
        // note that we're intentionally not touching fields here because this is
        // usually not called as a result of direct user action
        this.data.set(fieldName, fieldValue);
        this.emitter.emit("UPDATED_MODEL_VALUE", {
            fieldName: fieldName
        });
    }

    @action
    addSelectOption(selectViewModelName, optionViewModel) {
        if (this[selectViewModelName]) {
            this[selectViewModelName].options.push(optionViewModel);
        }
    }

    @action
    removeSelectOption(selectViewModelName, matchingFunction) {
        if (this[selectViewModelName]) {
            this[selectViewModelName].options = filter(this[selectViewModelName].options, option => !matchingFunction(option));
        }
    }

    @action
    prevalidate() {
        if (!isNull(this.parent)) {
            this.parent.updateSubformIndexes();
        }
    }

    @action
    updateSubformIndexes() {
        forEach(observableMap.values(this.data), value => {
            if (FormModel._isSubformArray(value)) {
                forEach(value, (subform, index) => {
                    subform.index = index;
                });
            }
        });
    }

    // aligns with Baobab implementation of form error handling; should rewrite form responses
    // when everything is in mobx
    @action
    _processErrorsFromServer() {
        // clear cached form data on error fields
        this._errorFieldsFromServer.clear();
        // eslint-disable-next-line lodash/prefer-lodash-method
        this._serverErrors.forEach((errorMessage, key) => {
            // store the data we submitted for fields that had server errors
            if (!isUndefined(this.data.get(key))) {
                this._errorFieldsFromServer.set(key, toJS(this.data.get(key)));
            }
            // set server errors on subforms
            const splitKey = split(key, ".");
            if (splitKey.length === 3) {
                const index = parseInt(splitKey[1], 10);
                const subformsName = splitKey[0];
                const subformErrors = this.get(subformsName)[index].errors;
                subformErrors.set(splitKey[2], errorMessage);
                forEach(subformErrors, (error, errorFieldName) => {
                    const subForm = this.get(subformsName);
                    subForm[index]._serverErrors.set(errorFieldName, error);
                });
            }
        });
    }

    @action
    onFormError() {
        this._onFormError && this._onFormError();
    }

    @action
    setFormErrorHandler(fn) {
        this._onFormError = fn;
    }

    @action
    clearErrors(fieldNames) {
        if (!fieldNames || isEmpty(fieldNames)) {
            this._doneFields.clear();
        } else {
            forEach(fieldNames, fieldName => {
                this._doneFields.delete(fieldName);
            });
        }
    }

    _callFunctionOnSubForm(functionName, ...functionArguments) {
        forEach(this.data, (value) => {
            if (FormModel._isSubform(value)) {
                if (isFunction(value[functionName])) {
                    value[functionName](...functionArguments);
                }
            } else if (FormModel._isSubformArray(value)) {
                forEach(value, (subform) => {
                    if (isFunction(subform[functionName])) {
                        subform[functionName](...functionArguments);
                    }
                });
            }
        });
    }

    static _isSubform(value) {
        return value instanceof FormModel;
    }

    static _isSubformArray(value) {
        return (!isUndefined(value) && !isNull(value) && isFunction(value.slice) && isArray(value.slice()));
    }
}
